import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Truck, RefreshCw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function DeliveryChallans() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [challans, setChallans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [newChallan, setNewChallan] = useState({
    customer_id: '', challan_date: new Date().toISOString().split('T')[0], delivery_address: '',
    items: [{ description: '', quantity: 1 }], notes: ''
  });

  useEffect(() => { fetchData(); }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [chRes, custRes] = await Promise.all([
        fetch(`${API}/api/sales/challans`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API}/api/sales/customers`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      if (chRes.ok) setChallans((await chRes.json()).challans || []);
      if (custRes.ok) {
        const data = await custRes.json();
        setCustomers([...(data.customers || []), ...(data.sub_customers || []).map(s => ({ ...s, customer_id: s.sub_customer_id }))]);
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const createChallan = async () => {
    if (!newChallan.customer_id || !newChallan.items[0].description) {
      toast.error('Please fill required fields'); return;
    }
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/sales/challans`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newChallan)
      });
      if (res.ok) { toast.success('Delivery challan created'); setShowNew(false); fetchData(); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch (error) { toast.error('Failed'); }
    finally { setProcessing(false); }
  };

  if (loading) return <div className="flex justify-center p-8"><RefreshCw className="h-6 w-6 animate-spin" /></div>;

  return (
    <Card className="mt-4">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2"><Truck className="h-5 w-5" />Delivery Challans</CardTitle>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />New Challan</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create Delivery Challan</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div><Label>Customer *</Label>
                  <Select value={newChallan.customer_id} onValueChange={(v) => setNewChallan({ ...newChallan, customer_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>{customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.company_name || c.contact_person}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Challan Date</Label><Input type="date" value={newChallan.challan_date} onChange={(e) => setNewChallan({ ...newChallan, challan_date: e.target.value })} /></div>
                <div><Label>Delivery Address</Label><Textarea value={newChallan.delivery_address} onChange={(e) => setNewChallan({ ...newChallan, delivery_address: e.target.value })} rows={2} /></div>
                <div className="border rounded p-3 bg-slate-50">
                  <Label className="text-sm font-medium">Item</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div><Label className="text-xs">Description *</Label><Input value={newChallan.items[0].description} onChange={(e) => setNewChallan({ ...newChallan, items: [{ ...newChallan.items[0], description: e.target.value }] })} /></div>
                    <div><Label className="text-xs">Qty</Label><Input type="number" min="1" value={newChallan.items[0].quantity} onChange={(e) => setNewChallan({ ...newChallan, items: [{ ...newChallan.items[0], quantity: parseInt(e.target.value) || 1 }] })} /></div>
                  </div>
                </div>
                <div><Label>Notes</Label><Textarea value={newChallan.notes} onChange={(e) => setNewChallan({ ...newChallan, notes: e.target.value })} rows={2} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
                <Button onClick={createChallan} disabled={processing}>{processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}Create</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Challan #</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {challans.map((c) => (
              <TableRow key={c.challan_id}>
                <TableCell className="font-medium">{c.challan_number}</TableCell>
                <TableCell>{c.challan_date}</TableCell>
                <TableCell>{c.customer_name}</TableCell>
                <TableCell>{c.items?.length || 0} items</TableCell>
                <TableCell><Badge variant={c.status === 'delivered' ? 'default' : 'outline'}>{c.status}</Badge></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {challans.length === 0 && <div className="text-center py-8 text-slate-500">No delivery challans</div>}
      </CardContent>
    </Card>
  );
}
